import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { legalDocuments } from "./legalContent";

const ACCENT = "#818CF8";

const LegalDocument = ({ documentType }) => {
  const [language, setLanguage] = useState("en");
  const legalDocument = legalDocuments[documentType] || legalDocuments.privacy;
  const content = legalDocument[language];

  useEffect(() => {
    window.document.title = `${content.title} | OneBotAds`;
    window.scrollTo(0, 0);
  }, [content.title]);

  return (
    <main style={{ minHeight: "100vh", background: "#F6F7FB", color: "#18181B" }}>
      <style>{`
        .legal-page * { box-sizing: border-box; }
        .legal-page a { color: #4338CA; }
        .legal-page a:hover { color: #312E81; }
        .legal-page p, .legal-page li { font-size: 15px; line-height: 1.75; }
        .legal-page li + li { margin-top: 9px; }
        .legal-page h2 { scroll-margin-top: 30px; }
        .legal-contents { column-count: 2; }
        @media (max-width: 640px) {
          .legal-page p, .legal-page li { font-size: 14px; line-height: 1.7; }
          .legal-contents { column-count: 1; }
        }
      `}</style>

      <div className="legal-page" style={{ maxWidth: 1040, margin: "0 auto", padding: "24px 20px 64px" }}>
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 38 }}>
          <Link
            to="/"
            style={{ color: "#171717", textDecoration: "none", fontWeight: 800, fontSize: 18, letterSpacing: "-0.03em" }}
          >
            OneBotAds
          </Link>
          <div aria-label="Language" style={{ display: "inline-flex", padding: 3, border: "1px solid #DCDDE7", borderRadius: 999, background: "#fff" }}>
            {[
              ["en", "English"],
              ["fr", "Français"],
            ].map(([code, label]) => (
              <button
                key={code}
                type="button"
                aria-pressed={language === code}
                onClick={() => setLanguage(code)}
                style={{
                  appearance: "none",
                  border: 0,
                  borderRadius: 999,
                  cursor: "pointer",
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  background: language === code ? "#221C51" : "transparent",
                  color: language === code ? "#fff" : "#52525B",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </header>

        <article style={{ background: "#fff", border: "1px solid #E3E4EC", borderRadius: 20, overflow: "hidden", boxShadow: "0 18px 60px rgba(15, 11, 45, 0.07)" }}>
          <div style={{ padding: "clamp(28px, 6vw, 62px)", background: "linear-gradient(135deg, #0F0B2D 0%, #211B50 100%)", color: "#fff" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 999, color: "rgba(255,255,255,0.72)", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT }} />
              {content.eyebrow}
            </div>
            <h1 style={{ margin: "20px 0 12px", maxWidth: 740, fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.03, letterSpacing: "-0.05em" }}>{content.title}</h1>
            <p style={{ margin: 0, maxWidth: 650, color: "rgba(255,255,255,0.72)", lineHeight: 1.65, fontSize: 15 }}>{content.summary}</p>
            <p style={{ margin: "24px 0 0", color: "rgba(255,255,255,0.48)", fontSize: 12 }}>{content.version}</p>
          </div>

          <div style={{ padding: "clamp(28px, 6vw, 62px)" }}>
            <section style={{ padding: "20px 22px", borderRadius: 14, background: "#F3F2FF", border: "1px solid #E5E4FF", marginBottom: 44 }}>
              <p style={{ margin: 0, fontWeight: 650, color: "#312E81" }}>{content.intro}</p>
            </section>

            <nav aria-label={language === "fr" ? "Sommaire" : "Contents"} style={{ marginBottom: 46 }}>
              <div style={{ color: "#71717A", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>{language === "fr" ? "Sommaire" : "Contents"}</div>
              <ol className="legal-contents" style={{ margin: 0, paddingLeft: 21, columnGap: 46 }}>
                {content.sections.map((section) => (
                  <li key={section.id} style={{ breakInside: "avoid", marginBottom: 7 }}>
                    <a href={`#${section.id}`} style={{ fontSize: 13, lineHeight: 1.5, textDecoration: "none" }}>{section.title}</a>
                  </li>
                ))}
              </ol>
            </nav>

            {content.sections.map((section, index) => (
              <section key={section.id} id={section.id} style={{ borderTop: index === 0 ? "none" : "1px solid #ECECF1", paddingTop: index === 0 ? 0 : 36, marginTop: index === 0 ? 0 : 36 }}>
                <h2 style={{ margin: "0 0 15px", fontSize: "clamp(21px, 3vw, 28px)", lineHeight: 1.18, letterSpacing: "-0.035em", color: "#18181B" }}>{section.title}</h2>
                {section.paragraphs?.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex} style={{ margin: paragraphIndex === 0 ? 0 : "14px 0 0" }}>{paragraph}</p>
                ))}
                {section.bullets?.length > 0 && (
                  <ul style={{ margin: "16px 0 0", paddingLeft: 23 }}>
                    {section.bullets.map((bullet, bulletIndex) => <li key={bulletIndex}>{bullet}</li>)}
                  </ul>
                )}
                {section.callout && (
                  <p style={{ margin: "18px 0 0", padding: "14px 16px", borderLeft: `3px solid ${ACCENT}`, background: "#FAFAFF", color: "#3F3F46", borderRadius: "0 9px 9px 0" }}>{section.callout}</p>
                )}
              </section>
            ))}

            <section style={{ marginTop: 44, padding: "22px 24px", borderRadius: 14, background: "#F7F7F8", border: "1px solid #E8E8EC" }}>
              <div style={{ fontWeight: 800, marginBottom: 7 }}>{content.contactTitle}</div>
              <p style={{ margin: 0 }}>{content.contactLead} <a href="mailto:contact@devtospace.com">contact@devtospace.com</a></p>
            </section>
          </div>
        </article>

        <footer style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", padding: "24px 4px 0", color: "#71717A", fontSize: 12 }}>
          <span>© {new Date().getFullYear()} OneBotAds · Devtospace Limited</span>
          <div style={{ display: "flex", gap: 16 }}>
            <Link to="/privacy-policy">{language === "fr" ? "Confidentialité" : "Privacy"}</Link>
            <Link to="/terms-of-service">{language === "fr" ? "Conditions" : "Terms"}</Link>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default LegalDocument;
